import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import { Link } from 'react-router-dom'

import {
  Form,
  TextInput,
  Grid,
  Row,
  Column,
  Button,
  Header,
  HeaderName,
  InlineNotification,
  HeaderGlobalBar
} from 'carbon-components-react'

import LanguageSwitch, { Languages } from '../LanguageSwitch'
import validate from './validateLogin'
import './index.scss'

function Login({
  title,
  submit,
  mapErrorToString,
  usernameLabel,
  passwordLabel,
  submitText,
  usernamePlaceholder,
  passwordPlaceholder,
  i18nControls,
  submitOnEnter: submitOnEnterProp,
  showPasswordLabel,
  hidePasswordLabel
}) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState({})
  const [authorized, setIsAuthorized] = useState(true)

  const handleSubmit = useCallback(() => {
    const errors = validate({ username, password })

    if (Object.keys(errors).length > 0) return setErrors(errors)

    setErrors({})
    try {
      submit(username, password)
    } catch (error) {
      setIsAuthorized(false)
    }
  }, [submit, username, password])

  useEffect(() => {
    if (!submitOnEnterProp) return

    const submitOnEnter = ({ key }) => {
      if (key === 'Enter') {
        handleSubmit()
      }
    }
    document.addEventListener('keydown', submitOnEnter)

    return () => {
      document.removeEventListener('keydown', submitOnEnter)
    }
  }, [handleSubmit])

  return (
    <div className='ublique-login'>
      <Header aria-label='Ublique'>
        <HeaderName
          element={Link}
          to='/'
          prefix=''
          className='ublique-login-logo'
        />
        {i18nControls && (
          <HeaderGlobalBar>
            <LanguageSwitch
              selectedLanguage={i18nControls.selectedLanguage}
              onChange={i18nControls.onLanguageChange}
              languages={i18nControls.languages}
              shortLanguage
            />
          </HeaderGlobalBar>
        )}
      </Header>
      <Grid>
        <Row className='row-login'>
          <Column />

          <Column className='col-login'>
            <Form>
              {title && (
                <div>
                  <h4 className='login-title'>{title}</h4>
                </div>
              )}
              {!authorized && (
                <InlineNotification
                  kind='error'
                  title=''
                  onCloseButtonClick={() => setIsAuthorized(true)}
                  subtitle={mapErrorToString('WrongAuthentication')}
                  iconDescription='Close button'
                />
              )}
              <TextInput
                className='login-username'
                value={username}
                id='username'
                name='username'
                invalid={!!errors.username}
                invalidText={mapErrorToString(errors.username)}
                labelText={usernameLabel}
                light
                onChange={({ target: { value } }) => setUsername(value)}
                placeholder={usernamePlaceholder || usernameLabel}
                required
                autoComplete='off'
              />

              <TextInput.PasswordInput
                className='login-password'
                value={password}
                id='password'
                name='password'
                invalid={!!errors.password}
                invalidText={mapErrorToString(errors.password)}
                labelText={passwordLabel}
                autoComplete='current-password'
                light
                onChange={({ target: { value } }) => setPassword(value)}
                placeholder={passwordPlaceholder || passwordLabel}
                // type='password'
                required
                showPasswordLabel={showPasswordLabel}
                hidePasswordLabel={hidePasswordLabel}
              />
            </Form>
          </Column>
          <Column />

          <div>
            <Button className='submit-button' onClick={handleSubmit}>
              {submitText}
            </Button>
          </div>
        </Row>
      </Grid>
    </div>
  )
}

Login.defaultProps = {
  submitOnEnter: true
}

Login.propTypes = {
  submit: PropTypes.func.isRequired,
  submitOnEnter: PropTypes.bool,
  title: PropTypes.string,
  logoSrc: PropTypes.node,
  usernameLabel: PropTypes.string.isRequired,
  usernamePlaceholder: PropTypes.string,
  passwordLabel: PropTypes.string.isRequired,
  passwordPlaceholder: PropTypes.string,
  submitText: PropTypes.string.isRequired,
  errorTitle: PropTypes.string,
  /**
   * Resolves error codes to meaningful messages for the user
   */
  mapErrorToString: PropTypes.func.isRequired,
  /**
   * If provided, shows and configs the language selector in the header
   */
  i18nControls: PropTypes.shape({
    languages: Languages.isRequired,
    selectedLanguage: PropTypes.string.isRequired,
    onLanguageChange: PropTypes.func.isRequired
  })
}

export default Login
